/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

import api from "@/config/api"
import { v4 as UUIDv4 } from "uuid"
import Vue from "vue"
import communication from "~/common/src/messaging"
import { predefinedParticipantProperties, defaultParticipantProperties } from "@/config/userPropertyConstants"

/* global log */

const loadStates = {
  UNLOADED: "unloaded",
  LOADING: "loading",
  LOADED: "loaded",
  ERROR: "error",
}
Object.freeze(loadStates)

const onlineStatus = ["OFFLINE", "IN_WAITING_ROOM", "IN_A_MEETING"]
Object.freeze(onlineStatus)

export { loadStates, onlineStatus }

const PAGE_SIZE = 30

/**
 * Builds a query string based on the search parameters provided. Excludes
 * query parameters not specified in search object.
 *
 * @param {Object} search
 * @param {number} pageSize Optional parameter specifying the size of the returned page (defaults to 30)
 * @returns {String} built from search parameters
 */
export const buildSearchQuery = function (search, pageSize = PAGE_SIZE) {
  let queryString = "?size=" + pageSize

  if (search) {
    if (search.onlineStatusFilters?.length) {
      queryString += "&onlineStatusFilters="
      search.onlineStatusFilters.forEach((o) => (queryString += o + ","))
      queryString = queryString.slice(0, queryString.length - 1)
    }

    if (search.properties?.length) {
      queryString += search.properties.reduce((final, prop) => {
        final += `${prop},`
        return final
      }, "&propertyKeys=")
      queryString = queryString.slice(0, queryString.length - 1)
    }

    if (search.values?.length) {
      queryString += search.values.reduce((final, prop) => {
        final += encodeURIComponent(`%${prop}%`) + ","
        return final
      }, "&propertyValue=")
      queryString = queryString.slice(0, queryString.length - 1)
    }
  }

  return queryString
}

/**
 * Performs minor property tweaking to ensure property values are correctly coerced into
 * the expected type specified by property object.
 *
 * Explicitly ensures favorite property type is always set to the known expected
 * property type and coerced value.
 *
 * @param {Object} participant
 * @returns {Object} of participant
 */
const mutateProperties = function (participant) {
  for (const property in participant.properties) {
    const predefinedProperty = predefinedParticipantProperties[property]
    if (predefinedProperty) {
      participant.properties[property].type = predefinedProperty.type
    }

    if (participant.properties[property].type === "BOOLEAN") {
      participant.properties[property].value = String(participant.properties[property].value).toLowerCase() === "true"
    }
  }

  for (const key of Object.values(defaultParticipantProperties)) {
    if (!participant.properties[key]) {
      const predefinedProperty = predefinedParticipantProperties[key]
      if (predefinedProperty) {
        participant.properties[key] = {
          value: predefinedProperty.value,
          type: predefinedProperty.type,
        }
      } else {
        participant.properties[key] = {
          value: "",
          type: predefinedProperty?.type ?? "STRING",
        }
      }
    }
  }

  return participant
}

/**
 * Provides a lambda to be used by publication subscription logic as a callback when a ParticipantCounts
 * publication is received. The lambda then uses the provided commit method to store the publication data into state.
 *
 * @param dispatch
 * @param {Function} commit
 * @param getters
 * @returns {Function}
 */
const handleStatusCountPublication = function (dispatch, commit, getters) {
  return (publication) => {
    let meetingId = publication?.channel?.meetingId
    if (!meetingId) {
      log.error("Cannot process publication count message with no meeting ID.")
    }
    let counts = publication?.data?.counts
    if (!counts) {
      log.error("Cannot process publication count message with no count map.")
    }

    let favoriteCount = publication?.data?.favoriteCount
    let totalCount = publication?.data?.totalCount

    // If an incoming participants count message sets the total count to be greater than zero,
    // trigger the load of the participants set from the server.
    // This is used to keep multiple MCCs in sync: if one MCC imports participants they are displayed in other MCCs
    // as well automatically if they don't have any participants loaded yet
    if (getters["getParticipantCount"](meetingId) === 0 && totalCount > 0) {
      dispatch("loadParticipants", { meetingId })
    }

    commit("setStatusCounts", {
      meetingId,
      counts,
      favoriteCount,
      totalCount,
    })
  }
}

/**
 * Provides a lambda to be used by publication subscription logic as a callback
 * when a ParticipantUpdates publication is received. The lambda then uses the provided
 * commit method to store the publication data into state.
 *
 * @param {Function} commit
 */
const handleParticipantUpdatesPublication = function (commit) {
  return (publication) => {
    let meetingId = publication?.channel?.meetingId
    if (!meetingId) {
      log.error("Cannot process publication count message with no meeting ID.")
    }
    for (const participantId in publication.data.updatedUsers) {
      // Make copy as this is technically a state element in another store
      let participant = JSON.parse(JSON.stringify(publication.data.updatedUsers[participantId]))
      mutateProperties(participant)

      commit("setParticipant", { meetingId, participant })
    }

    for (const participantId of publication.data.deletedUsers) {
      commit("removeParticipant", { meetingId, participantId })
    }
  }
}

export default {
  namespaced: true,

  state: {
    pages: {}, // Maps meetingIds to current page index for participant lists
    participants: {}, // Maps meetingIds to participant lists
    statusCounts: {}, // MeetingId to map of status (OnlineStatus, favorite, total) to number
    status: {},
    correlationIds: {},
  },

  mutations: {
    /**
     * commits load status to state
     *
     * @param state
     * @param meetingId
     * @param status
     */
    setLoadState(state, { meetingId, status }) {
      Vue.set(state.status, meetingId, status)
    },

    setCorrelationId(state, { meetingId, correlationId }) {
      Vue.set(state.correlationIds, meetingId, correlationId)
    },

    /**
     * commits participant set and current page information to state
     *
     * @param state
     * @param meetingId
     * @param participants
     * @param page
     */
    setParticipants(state, { meetingId, participants, page }) {
      // Store current page index info for participants
      Vue.set(state.pages, meetingId, {
        currentPage: page.number,
        totalPages: page.totalPages,
      })

      // Store initial meeting object to store participants into
      Vue.set(state.participants, meetingId, {})

      // Store initial participant set
      participants.forEach((p) => {
        p["id"] = p.userId
        Vue.set(state.participants[meetingId], p.userId, p)
      })
    },

    /**
     * commits participant information to state and calls for update of count
     *
     * @param state
     * @param meetingId
     * @param participant
     */
    setParticipant(state, { meetingId, participant }) {
      log.debug("Setting participant into store: ", JSON.stringify(participant))

      if (!state.participants[meetingId]) {
        Vue.set(state.participants, meetingId, {})
      }

      participant["id"] = participant.userId
      Vue.set(state.participants[meetingId], participant.userId, participant)
    },

    setStatusCounts(state, { meetingId, counts, favoriteCount, totalCount }) {
      log.debug("Received update of participant status counts for meeting:", meetingId, ":", JSON.stringify(counts))

      if (!state.statusCounts[meetingId]) {
        Vue.set(state.statusCounts, meetingId, {})
      }

      Vue.set(state.statusCounts[meetingId], "favorite", favoriteCount)
      Vue.set(state.statusCounts[meetingId], "total", totalCount)

      onlineStatus.forEach((status) => {
        // Don't use if (counts[status]) here because zero is interpreted as false :-)
        if (counts.hasOwnProperty(status)) {
          Vue.set(state.statusCounts[meetingId], status, counts[status])
        }
      })
    },

    /**
     * commits participant properties to an existing participants state
     *
     * @param state
     * @param meetingId
     * @param participantId
     * @param properties
     */
    setParticipantProperties(state, { meetingId, participantId, properties }) {
      if (!state.participants[meetingId]) {
        log.warn("Cannot access meeting with ID - ", meetingId, " - does not exist. Skipping commit.")
        return
      }
      if (!state.participants[meetingId][participantId]) {
        log.warn("Participant with ID - ", participantId, " - does not exist. Skipping commit.")
        return
      }

      log.debug("Setting participant properties onto: ", participantId, " : ", properties)
      for (const key of Object.keys(properties)) {
        // Use Vue set on the off chance a non-primitive is being assigned
        Vue.set(state.participants[meetingId][participantId].properties, key, properties[key])
      }
    },

    /**
     * commits reset of all participant data and associating page and count information to state.
     *
     * @param state
     * @param meetingId
     */
    removeParticipants(state, { meetingId }) {
      log.debug(
        "Removing all participants: ",
        Object.keys(state.participants[meetingId]).length,
        " participants will be removed."
      )
      Vue.delete(state.participants, meetingId)

      // Reset the default state
      Vue.set(state.participants, meetingId, {})

      // Reset current page index for participants
      Vue.set(state.pages, meetingId, {})

      // Total number of participants is updated via subscriptions
      // (and if there are participants, we are subscribed to the status counts; see CockpitLayout::created())
    },

    /**
     * commits removal of a participant to state and calls for update of count
     *
     * @param state
     * @param participantId
     */
    removeParticipant(state, { meetingId, participantId }) {
      if (!state.participants[meetingId]) {
        log.warn("Cannot access meeting with ID - ", meetingId, " - does not exist. Skipping commit.")
        return
      }
      if (!state.participants[meetingId][participantId]) {
        log.warn("Participant with ID - ", participantId, " - does not exist. Skipping commit.")
        return
      }

      log.debug("Removing participant - ", participantId, " - from store.")
      Vue.delete(state.participants[meetingId], participantId)

      // Total number of participants is updated via subscriptions
      // (and if there are participants, we are subscribed to the status counts; see CockpitLayout::created())
    },
  },

  actions: {
    /**
     * Facilitates the load of a set of participants from server based on search
     * query information if provided.
     * Important: In the current implementation, this method must be used only from ParticipantList.vue; for other
     * situations, use api directly. todo: refactor this to be more generic (see todo comment in UserDialogParticipant)
     *
     * @param dispatch
     * @param commit
     * @param getters
     * @param meetingId
     * @param search
     * @returns {Promise}
     */
    loadParticipants({ dispatch, commit, getters }, { meetingId, search }) {
      const correlationId = UUIDv4()
      commit("setCorrelationId", { meetingId, correlationId })

      return new Promise((resolve, reject) => {
        commit("setLoadState", { meetingId, status: loadStates.LOADING })

        Vue.http
          .get(api.MEETING_PARTICIPANTS(meetingId) + buildSearchQuery(search), {
            headers: {
              "X-Correlation-Id": correlationId,
            },
          })
          .then(
            (success) => {
              const reqCorrelationId = success.headers.get("X-Correlation-Id")
              if (reqCorrelationId && reqCorrelationId !== getters["getCorrelationId"](meetingId)) {
                log.warn("Search result with id " + reqCorrelationId + " was obsolete on arrival - ignoring")
                // Resolve so we don't break Vue - different control flow than PagedRequestHandler!
                resolve()
                return
              }

              let payload = success.body
              // Mutate properties to ensure the format of properties per the stored type information
              // is correct.
              for (const participant of payload.content) {
                mutateProperties(participant)
              }

              let participantCount = getters["getParticipantCount"](meetingId)
              if (participantCount) {
                log.info("Unsubscribing updates of existing participant from previous load request.")
                dispatch("unregisterFromParticipantUpdates", { meetingId })
              }

              commit("setParticipants", {
                meetingId,
                participants: payload.content,
                page: {
                  number: payload.number,
                  totalPages: payload.totalPages,
                },
              })

              // Subscribe to the new set of participants.
              // Register dispatch already queries the most recent set held in store.
              dispatch("registerForParticipantUpdates", {
                meetingId,
                callback: handleParticipantUpdatesPublication(commit),
              })

              // Initially set the participant count if it's not set yet (triggers display of them in the ParticipantList)
              if (!participantCount) {
                commit("setStatusCounts", {
                  meetingId,
                  counts: {},
                  favoriteCount: 0,
                  totalCount: payload.totalElements,
                })
              }

              // update status
              commit("setLoadState", { meetingId, status: loadStates.LOADED })

              log.debug(`Finished loading participants from server.`)

              resolve()
            },
            (error) => {
              // update status
              commit("setLoadState", { meetingId, status: loadStates.ERROR })

              // Log error
              log.error(`${error.status} : ${JSON.stringify(error.body)}`)

              reject()
            }
          )
      })
    },

    /**
     * Facilitates the load of the next page of participants from server filtering
     * based on search query information if provided.
     *
     * @param {String} meetingId
     * @param {Object} search
     * @returns {Promise}
     */
    loadNextPage({ dispatch, commit, getters }, { meetingId, search }) {
      const correlationId = UUIDv4()
      commit("setCorrelationId", { meetingId, correlationId })
      return new Promise((resolve, reject) => {
        commit("setLoadState", { meetingId, status: loadStates.LOADING })

        Vue.http
          .get(
            api.MEETING_PARTICIPANTS(meetingId) +
              buildSearchQuery(search) +
              "&page=" +
              (getters["getCurrentPageInfo"](meetingId).currentPage + 1),
            {
              headers: {
                "X-Correlation-Id": correlationId,
              },
            }
          )
          .then(
            (success) => {
              const reqCorrelationId = success.headers.get("X-Correlation-Id")
              if (reqCorrelationId && reqCorrelationId !== getters["getCorrelationId"](meetingId)) {
                log.warn("Search result with id " + reqCorrelationId + " was obsolete on arrival - ignoring")
                // Resolve so we don't break Vue - different control flow than PagedRequestHandler!
                resolve()
                return
              }

              let payload = success.body
              for (const participant of payload.content) {
                mutateProperties(participant)
              }

              let currentParticipants = getters["getParticipants"](meetingId)

              currentParticipants = currentParticipants.concat(payload.content)

              commit("setParticipants", {
                meetingId,
                participants: currentParticipants,
                page: {
                  number: payload.number,
                  totalPages: payload.totalPages,
                },
              })

              dispatch("registerForParticipantUpdates", {
                meetingId,
              })

              // update status
              commit("setLoadState", { meetingId, status: loadStates.LOADED })

              log.debug(`Finished loading ${payload.content.length} additional participants from server.`)

              resolve(currentParticipants.length)
            },
            (error) => {
              // update status
              commit("setLoadState", { meetingId, status: loadStates.ERROR })

              // Log error
              log.error(`${error.status} : ${JSON.stringify(error.body)}`)

              reject(error)
            }
          )
      })
    },

    /**
     * Facilitates the process of creating a new participant on server. The value
     * of the shallSet flag indicates whether the response from the server shall be
     * committed directly to state, or if the update to state will occur via an alternative
     * means.
     *
     * @param {String} meetingId
     * @param {Object} participant
     * @param {Boolean} shallSet
     * @returns {Promise}
     */
    createParticipant({ dispatch, commit }, { meetingId, participant, shallSet = false }) {
      return new Promise((resolve, reject) => {
        // Mutate userProperties back to what the server expects
        mutateProperties(participant)

        let today = new Date()
        // Configure default values expected on the user object
        participant["enabled"] = true
        participant["createdDate"] = today.toISOString()
        participant["lastModifiedDate"] = today.toISOString()

        log.info("Dispatching newly configured participant to server: ", JSON.stringify(participant))
        // ---- Send to server ----
        Vue.http.post(api.MEETING_PARTICIPANTS(meetingId), participant).then(
          (success) => {
            log.debug("New participant created on server, setting to store.")
            let response = success.body

            mutateProperties(response)

            // Typically, due to the use of pagination, the list should be reloaded. However,
            // if this is not desired add the user and request updates.
            if (shallSet) {
              commit("setParticipant", { meetingId, participant: response })

              dispatch("registerForParticipantUpdates", {
                meetingId,
                callback: handleParticipantUpdatesPublication(commit),
              })

              // Total number of participants is updated via subscriptions
              // (and if there are participants, we are subscribed to the status counts; see CockpitLayout::created())
            }

            resolve(response)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Facilitates the process of updating a complete participant object on server.
     * After this operation the entire participant object is replaced by the resultant
     * participant.
     *
     * @param {String} meetingId
     * @param {Object} participant
     * @returns {Promise}
     */
    updateParticipant({ commit }, { meetingId, participant }) {
      return new Promise((resolve, reject) => {
        delete participant.id
        Vue.http.put(api.MEETING_PARTICIPANT(meetingId, participant.userId), participant).then(
          (success) => {
            log.debug("Participant successfully updated on server, setting to store.")
            mutateProperties(success.body)

            commit("setParticipant", {
              meetingId,
              participant: success.body,
            })

            resolve(participant)
          },
          (error) => {
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Facilitates the process of updating a property of a participant on server.
     *
     * @param {String} meetingId
     * @param {String} participantId
     * @param {String} property
     * @param {String} value
     * @returns {Promise}
     */
    updateParticipantProperty({ commit }, { meetingId, participantId, property, value }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETING_PARTICIPANT_PROPERTY(meetingId, participantId, property), value).then(
          (success) => {
            log.debug("Participant properties successfully updated on server, setting to store.")
            let pseudoParticipant = {
              properties: {
                [property]: success.body,
              },
            }

            commit("setParticipantProperties", {
              meetingId,
              participantId,
              properties: pseudoParticipant.properties,
            })

            resolve(success.body)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Facilitates the process of deleting all participants from server and state.
     * This process includes requesting an unsubscription of the deleted participants from
     * publications.
     *
     * @param {String} meetingId
     * @returns {Promise}
     */
    deleteParticipants({ dispatch, commit }, { meetingId }) {
      return new Promise((resolve, reject) => {
        // Unsubscribe from all participants prior to deletion to ensure deleted user publications are
        // not received by us.
        dispatch("unregisterFromParticipantUpdates", { meetingId })

        Vue.http.delete(api.MEETING_PARTICIPANTS_BULK(meetingId) + "?ids=*").then(
          (success) => {
            commit("removeParticipants", { meetingId })
            resolve(success.status) // delete endpoint does not provide response payload
          },
          (error) => {
            log.debug("Requesting to be resubscribed to publications for participants")

            // Re-add subscription after failure
            dispatch("registerForParticipantUpdates", { meetingId })

            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Facilitates the process of deleting a participant from server and state.
     *
     * @param {String} meetingId
     * @param {String} participantId
     * @returns {Promise}
     */
    deleteParticipant({ commit }, { meetingId, participantId }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.MEETING_PARTICIPANT(meetingId, participantId)).then(
          (success) => {
            commit("removeParticipant", { meetingId, participantId })

            resolve(success) // delete endpoint does not provide response payload
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Accredit a user
     *
     * @param {String} meetingId
     * @param {String} participantId
     * @returns {Promise}
     */
    accreditParticipant(_, { meetingId, participantId, value }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETING_PARTICIPANT_ACCREDITATION(meetingId, participantId), value).then(
          (success) => {
            resolve(success)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    /**
     * Facilitates the process of requesting the logout of participants from remote devices
     * If no participantIds are provided the request will be sent with only the meetingId
     * and the server will assume all participants are to be logged out.
     *
     * @param {String} meetingId
     * @param {Array} participantIds
     */
    // eslint-disable-next-line no-empty-pattern
    logoutParticipants({}, { meetingId, participantIds }) {
      log.info("-------- Processing request to log out participants from id: ", meetingId, participantIds)
      let messageBlock = {
        meetingId,
      }
      if (participantIds) {
        messageBlock["participantIds"] = participantIds
      }

      communication.sendMessage({
        messageType: "LogoutMessage",
        ...messageBlock,
      })
    },

    /**
     * Facilitates subscribing to participant updates through the publication mechanism.
     * A callback can be provided as a method of being informed when a new publication has been processed.
     *
     * To function correctly with the publication mechanism, a complete set of participants held
     * in store is always used. Thus this should be dispatched only after additions have been committed
     * to state.
     *
     * @param {Function} callback
     * @param {String} meetingId
     */
    registerForParticipantUpdates({ dispatch, getters }, { callback, meetingId }) {
      // Due to the need by the publication mechanism of always sending a complete list of participants
      // for each subscription, simply grab the most recent set in store.
      const participants = getters["getParticipants"](meetingId).reduce((result, participant) => {
        result.push(participant.userId)
        return result
      }, [])

      // getParticipants always returns an array structure
      if (participants.length) {
        log.debug(`Requesting subscription to publications for participants: ${JSON.stringify(participants)}`)

        dispatch(
          "publications/subscribeToPublication",
          {
            pubType: "ParticipantUpdates",
            newChannelConfig: {
              meetingId,
              participants,
            },
            callback,
          },
          { root: true }
        )
      }
    },

    /**
     * Facilitates unsubscribing from participant updates through the publication mechanism.
     *
     * To function correctly with the publication mechanism, a complete set of participants held
     * in store is always used. Thus this should be dispatched only after deletions have been committed
     * to state.
     *
     * @param {String} meetingId
     */
    unregisterFromParticipantUpdates({ dispatch, getters }, { meetingId }) {
      // Since the register dispatch subscribes to the most recent set of participants in store,
      // simply send the participant set in the same manner.
      const participants = getters["getParticipants"](meetingId).reduce((result, participant) => {
        result.push(participant.userId)
        return result
      }, [])

      // getParticipants always returns an array structure
      if (participants.length) {
        log.debug(
          `Requesting removal of subscription to publications for participants: ${JSON.stringify(participants)}`
        )

        dispatch(
          "publications/unsubscribeFromPublication",
          {
            pubType: "ParticipantUpdates",
            targetChannelConfig: {
              meetingId,
              participants,
            },
          },
          { root: true }
        )
      }
    },

    participantAdmission({ commit }, { meetingId, userIds, closeWaitingRoom }) {
      let config = { params: { closeWaitingRoom: closeWaitingRoom } }
      if (userIds) {
        config.params.ids = userIds.toString()
      }
      return new Promise((resolve, reject) => {
        Vue.http.post(api.LET_USERS_IN(meetingId), null, config).then(
          (success) => {
            if (closeWaitingRoom) {
              commit("meetings/closeWaitingRoom", { id: meetingId }, { root: true })
            }
            // Nothing else to do here
            resolve(success)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    subscribeForParticipantCounts({ dispatch, commit, getters }, { meetingId }) {
      log.debug("Subscribe for total count and online status counts for meeting", meetingId)
      dispatch(
        "publications/subscribeToPublication",
        {
          pubType: "ParticipantCounts",
          newChannelConfig: {
            meetingId,
          },
          callback: handleStatusCountPublication(dispatch, commit, getters),
        },
        { root: true }
      )
    },
  },

  getters: {
    /**
     * Returns current load state of activities on store. This includes intial loads
     * as well as loading of next pages.
     *
     * @param {*} state
     * @returns String
     */
    getLoadState: (state) => (meetingId) => state.status[meetingId] || loadStates.UNLOADED,

    getCorrelationId: (state) => (meetingId) => state.correlationIds[meetingId],

    /**
     * Returns the current page info. This page info is used internally when processing a
     * next page load event.
     *
     * @param {*} state
     * @param {String} meetingId
     * @returns Object
     */
    getCurrentPageInfo: (state) => (meetingId) => {
      return state.pages[meetingId] || {}
    },

    /**
     * Retrieves the current participant set for a given meeting formatted as an array.
     *
     * @param {*} state
     * @param {String} meetingId
     * @returns {Array}
     */
    getParticipants: (state) => (meetingId) => {
      // Return every participant
      return state.participants[meetingId] ? Object.values(state.participants[meetingId]) : []
    },

    /**
     * Retrieves the participant object within the indicated meeting specified by the given participant id.
     *
     * @param {String} meetingId
     * @returns {Object}
     */
    getParticipant: (state) => (meetingId, id) => {
      // Attempt look up
      let participant = null
      if (state.participants[meetingId] && state.participants[meetingId][id]) {
        participant = state.participants[meetingId][id]
      }

      return participant
    },

    /**
     * Returns the current participant count
     *
     * @param {*} state
     * @param {String} meetingId
     * @returns {Number}
     */
    getParticipantCount: (state) => (meetingId) => state.statusCounts[meetingId]?.total || 0,

    /**
     * Returns the current number of participants marked as favorite.
     *
     * @param state
     * @return {Number}
     */
    getFavoriteCount: (state) => (meetingId) => state.statusCounts[meetingId]?.favorite || 0,

    /**
     * Returns the current numbers of online status counts of the given meeting.
     *
     * @param state
     * @return {Object} An object containing the online status constants mapped to a Number, or an empty object if
     *                  the online status information is not yet available
     */
    getStatusCounts: (state) => (meetingId) => state.statusCounts[meetingId] || {},

    /**
     * Returns the current number of participants in the given meeting having the given online status.
     *
     * @param state
     * @return {Number}
     */
    getStatusCount: (state) => (meetingId, status) => {
      let counts = state.statusCounts[meetingId]
      if (!counts || !counts.hasOwnProperty(status)) {
        return 0
      }
      return counts[status]
    },
  },
}
